import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, LogOut } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { compareEventsByNearestStart } from "@mvk/shared";
import { useAppStore } from "../store/useAppStore";
import { maxPlatform } from "../services/maxPlatform";
import { eventsService } from "../services/eventsService";
import { visitorAuthService } from "../services/visitorAuthService";
import { isApiEnabled } from "../api/config";
import { EventPosterCard } from "../components/EventPosterCard";
import { ClaimExhibitorEntryCard } from "../components/ClaimExhibitorEntryCard";
import type { Event } from "../types";

export function OnboardingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { selectEvent, completeOnboarding, selectedEventId, onboardingComplete, reset } =
    useAppStore();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [pastExpanded, setPastExpanded] = useState(false);
  const PAST_PREVIEW = 3;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        eventsService.clearCache();
        const fromStart = maxPlatform.resolveEventIdFromStartParam();
        const list = await eventsService.getAll();
        if (cancelled) return;

        // Resume last exhibition (or start_param / sole access) — skip picker.
        // Prefer exhibitor / nearest non-past over a stale saved show when the
        // user gained new access (e.g. Beauty badge while Урал was last opened).
        const resolved = await eventsService.resolveLaunchEvent(fromStart);
        const skipTo =
          fromStart && resolved
            ? resolved
            : list.length === 1
              ? list[0]
              : onboardingComplete && resolved && resolved.status !== "past"
                ? resolved
                : null;
        if (skipTo) {
          selectEvent(skipTo.id);
          completeOnboarding();
          navigate("/home", { replace: true });
          return;
        }

        setEvents(list);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [completeOnboarding, i18n.language, navigate, onboardingComplete, selectEvent]);

  const { activeEvents, upcomingEvents, pastEvents } = useMemo(() => {
    const byStart = (list: Event[]) => [...list].sort(compareEventsByNearestStart);
    const active = byStart(events.filter((e) => e.status === "active"));
    const upcoming = byStart(events.filter((e) => e.status === "upcoming"));
    const past = [...events.filter((e) => e.status === "past")].sort(
      (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime(),
    );
    return { activeEvents: active, upcomingEvents: upcoming, pastEvents: past };
  }, [events]);

  const handleSelect = (event: Event) => {
    maxPlatform.hapticFeedback("medium");
    selectEvent(event.id);
    completeOnboarding();
    navigate("/home", { replace: true });
  };

  const handleLogout = async () => {
    const confirmed = maxPlatform.isAvailable()
      ? true
      : await maxPlatform.showConfirm(t("profile.logout_confirm"));
    if (!confirmed) return;
    maxPlatform.hapticFeedback("heavy");
    if (isApiEnabled()) {
      visitorAuthService.logout();
      eventsService.clearCache();
    }
    reset();
    navigate(isApiEnabled() ? "/login" : "/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 via-white to-slate-50 text-gray-400">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-slate-50 flex flex-col">
      <div className="pt-[env(safe-area-inset-top)]">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="px-5 pt-8 pb-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <img
              src="/icons/app-icon.png?v=20260916"
              alt="MVK One"
              width={32}
              height={32}
              className="h-8 w-8 rounded-lg shadow-sm"
            />
            <span className="text-[13px] text-indigo-600">MVK One</span>
          </div>
          <h1 className="text-[26px] text-gray-900 mb-1">{t("onboarding.title")}</h1>
          {t("onboarding.subtitle") ? (
            <p className="text-[15px] text-gray-500">{t("onboarding.subtitle")}</p>
          ) : null}
        </motion.div>
      </div>

      <div className="px-4 pb-4 space-y-6 flex-1">
        <ClaimExhibitorEntryCard />

        {events.length === 0 && (
          <p className="text-center text-[14px] text-gray-500 py-8">
            {t("onboarding.empty")}
          </p>
        )}

        {activeEvents.length > 0 && (
          <section>
            <p className="text-[12px] text-gray-400 uppercase tracking-wider mb-2 px-1">
              {t("onboarding.section_active")}
            </p>
            <div className="space-y-3">
              {activeEvents.map((event) => (
                <EventPosterCard
                  key={event.id}
                  event={event}
                  onSelect={handleSelect}
                  current={event.id === selectedEventId}
                />
              ))}
            </div>
          </section>
        )}

        {upcomingEvents.length > 0 && (
          <section>
            <p className="text-[12px] text-gray-400 uppercase tracking-wider mb-2 px-1">
              {t("onboarding.section_upcoming")}
            </p>
            <div className="space-y-3">
              {upcomingEvents.map((event) => (
                <EventPosterCard
                  key={event.id}
                  event={event}
                  onSelect={handleSelect}
                  current={event.id === selectedEventId}
                />
              ))}
            </div>
          </section>
        )}

        {pastEvents.length > 0 && (
          <section>
            <p className="text-[12px] text-gray-400 uppercase tracking-wider mb-2 px-1">
              {t("onboarding.section_archive")}
            </p>
            <div className="space-y-3">
              {(pastExpanded ? pastEvents : pastEvents.slice(0, PAST_PREVIEW)).map((event) => (
                <EventPosterCard
                  key={event.id}
                  event={event}
                  onSelect={handleSelect}
                  current={event.id === selectedEventId}
                />
              ))}
            </div>
            {pastEvents.length > PAST_PREVIEW && (
              <button
                type="button"
                onClick={() => setPastExpanded((v) => !v)}
                className="mt-3 w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-[13px] text-indigo-600"
              >
                {pastExpanded
                  ? t("onboarding.past_collapse")
                  : t("onboarding.past_expand", { n: pastEvents.length - PAST_PREVIEW })}
              </button>
            )}
          </section>
        )}
      </div>

      <div className="px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-2">
        <button
          type="button"
          onClick={() => void handleLogout()}
          className="w-full flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-[14px] text-gray-600 active:scale-[0.99] transition-transform"
        >
          <LogOut size={16} className="text-red-500" />
          {t("onboarding.logout")}
        </button>
      </div>
    </div>
  );
}
